import React, { useMemo } from "react"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import Select from "@mui/material/Select"
import OutlinedInput from "@mui/material/OutlinedInput"
import MenuItem from "@mui/material/MenuItem"
import Checkbox from "@mui/material/Checkbox"
import ListItemText from "@mui/material/ListItemText"
import { makeStyles } from "tss-react/mui"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useSupplier } from "../../../../supplierHooks"
import { ListSubheader } from "@mui/material"

import {
    fetchSatellitesForSupplierParams,
    SatellitesTypesForSupplierResponse,
    SupplierSatellitesQuery,
} from "../../satellites/satellitesApi"
import { MenuProps } from "../../../../../../shared/components/filters/constants/filterMenuConst"

type MerchantSatellitesFilterProps = Readonly<{
    selectedIds: string[]
    setSelectedIds: (value: string[]) => void
    merchantIds?: string[]
}>

const useStyles = makeStyles()(() => ({
    input: {
        width: "20rem",
    },
}))

function transformData(data: SatellitesTypesForSupplierResponse) {
    return data.merchants
}

export function MultiMerchantSatellitesFilter({
    merchantIds,
    selectedIds,
    setSelectedIds,
}: MerchantSatellitesFilterProps) {
    const { classes } = useStyles()

    const { supplierId } = useSupplier()

    const { data } = useApiQuery(fetchSatellitesForSupplierParams, {
        queryName: SupplierSatellitesQuery.SATELLITES_LIST,
        dependencies: {
            supplierId,
            merchantIds: merchantIds?.join(",") ?? "",
        },
        enabled: Boolean(merchantIds?.length),
        select: transformData,
    })

    const satellitesMap = useMemo(() => {
        const map: { [id: string]: { name: string; merchantName: string } } = {}
        data?.forEach((merchant) =>
            merchant.satellites.forEach((satellite) => {
                map[satellite.id] = { name: satellite.name, merchantName: merchant.name }
            }),
        )
        return map
    }, [data])

    const handleMenuItemClick = (satelliteId: string) => {
        const selectedIndex = selectedIds.indexOf(satelliteId)
        let newSelectedIds: string[] = []

        if (selectedIndex === -1) {
            newSelectedIds = [...selectedIds, satelliteId]
        } else {
            newSelectedIds = selectedIds.filter((id) => id !== satelliteId)
        }

        setSelectedIds(newSelectedIds)
    }

    return (
        <FormControl className={classes.input}>
            <LocalizedStrict id="supplier-meeting-service-drawer-satellites-label">
                <InputLabel id="multiple-checkbox-satellites-label">Satellites</InputLabel>
            </LocalizedStrict>
            <Select
                labelId="multiple-checkbox-satellites-label"
                id="multiple-checkbox-satellites"
                multiple
                disabled={!Boolean(merchantIds?.length) || !data}
                value={selectedIds}
                input={<OutlinedInput label="Satellites" />}
                renderValue={(selected) =>
                    selected
                        .map(
                            (satelliteId) =>
                                `${satellitesMap[satelliteId].name} (${satellitesMap[satelliteId].merchantName})` || "",
                        )
                        .join(", ")
                }
                MenuProps={MenuProps}
            >
                {data?.map((merchant) => (
                    <React.Fragment key={`${merchant.name}-header`}>
                        <ListSubheader>{merchant.name}</ListSubheader>
                        {merchant.satellites.map((satellite) => (
                            <MenuItem
                                key={satellite.id}
                                value={satellite.id}
                                onClick={() => handleMenuItemClick(satellite.id.toString())}
                            >
                                <Checkbox checked={selectedIds.indexOf(satellite.id.toString()) > -1} />
                                <ListItemText primary={satellite.name} />
                            </MenuItem>
                        ))}
                    </React.Fragment>
                ))}
            </Select>
        </FormControl>
    )
}

